import { IS_DEVELOPMENT } from "@/constants/env";
import { SideNav } from "@/types/navigation";

export const getNavStorage = () => {
  if (typeof window === "undefined") return {};
  const navStorage = localStorage.getItem("navStorage");
  return navStorage ? JSON.parse(navStorage) : {};
};

export const getActivePageIds = (pathname: string) => {
  const normalizedPath = pathname?.startsWith("/v/")
    ? pathname.slice(2)
    : pathname;
  const parts = normalizedPath?.split("/").filter(Boolean) || [];
  const [, pageId, subPageId] = parts;

  const currentPage = navItems.find(({ id }) => id === pageId);
  const currentSubPage = currentPage?.subItems.find(
    ({ href }) => href === `/${pageId}/${subPageId}`,
  );

  let activeSubPageId = "";

  if (currentSubPage) {
    activeSubPageId = currentSubPage.id;
  } else {
    activeSubPageId = currentPage?.subItems?.[0]?.id || "";
  }

  return [pageId, activeSubPageId];
};

export const getNavLabel = (id: string): string => {
  for (const navItem of navItems) {
    if (navItem.id === id) return navItem.label;
    const subItem = navItem.subItems.find((sub) => sub.id === id);
    if (subItem) return subItem.label;
  }
  return id;
};

const navItems: SideNav = [
  {
    id: "analytics",
    label: "Analytics",
    icon: "analytics",
    scopes: [],
    subItems: [
      { id: "overview", label: "Overview", href: "/analytics", scopes: [] },
      {
        id: "subscriptions",
        label: "Subscriptions",
        href: "/analytics/subscriptions",
        scopes: [],
      },
      {
        id: "single_purchases",
        label: "Single Purchases",
        href: "/analytics/purchases",
        scopes: [],
      },
      {
        id: "podcasts",
        label: "Podcasts",
        href: "/analytics/podcasts",
        scopes: ["sesamy:developer"],
      },
    ],
  },
  {
    id: "podcasts",
    label: "Podcasts",
    icon: "podcasts",
    scopes: ["podcasts:read", "podcasts:write"],
    subItems: [
      { id: "shows", label: "Shows", href: "/podcasts/shows", scopes: [] },
      {
        id: "purchase_options",
        label: "Purchase options",
        href: "/podcasts/purchase-options",
        scopes: [],
      },
    ],
  },
  {
    id: "paywalls",
    label: "Paywalls",
    icon: "paywalls",
    scopes: ["paywall:read", "paywall:write"],
    subItems: [
      { id: "paywalls", label: "Paywalls", href: "/paywalls", scopes: [] },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: "customers",
    scopes: [],
    subItems: [
      { id: "customers", label: "Customers", href: "/customers", scopes: [] },
      {
        id: "external_customers",
        label: "External Customers",
        href: "/customers/external-customers",
        scopes: ["external-origins:external-purchases:read"],
      },
    ],
  },
  {
    id: "contracts",
    label: "Contracts",
    icon: "receipt",
    scopes: [],
    subItems: [
      {
        id: "contracts",
        label: "Contracts",
        href: "/contracts",
        scopes: [],
      },
      { id: "bills", label: "Bills", href: "/contracts/bills", scopes: [] },
      {
        id: "manual_payments",
        label: "Manual Payments",
        href: "/contracts/manual-payments",
        scopes: [],
      },
    ],
  },
  {
    id: "deliveries",
    label: "Deliveries",
    icon: "shopping-cart",
    scopes: ["fulfillment:read"],
    subItems: [
      {
        id: "deliveries",
        label: "Deliveries",
        href: "/deliveries",
        scopes: ["fulfillment:read"],
      },
    ],
  },
  {
    id: "products",
    label: "Products",
    icon: "package",
    scopes: [],
    subItems: [
      {
        id: "products",
        label: "Products",
        href: "/products",
        scopes: [],
        basePath: "/v",
        featureFlag: "products-view",
      },
    ],
  },
  {
    id: "discounts",
    label: "Discounts",
    icon: "discounts",
    scopes: ["discounts:checkout:read"],
    subItems: [
      { id: "discounts", label: "Discounts", href: "/discounts", scopes: [] },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: "reports",
    scopes: [],
    subItems: [
      { id: "reports", label: "Reports", href: "/reports", scopes: [] },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    icon: "tools",
    scopes: [],
    subItems: [{ id: "tools", label: "Tools", href: "/tools", scopes: [] }],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "settings",
    scopes: ["profile:vendors:read", "profile:vendor:manage-self"],
    subItems: [
      {
        id: "settings",
        label: "Settings",
        href: "/settings/business",
        scopes: [],
      },
      {
        id: "integrations",
        label: "Integrations",
        href: "/settings/integrations",
        scopes: [],
        featureFlag: "settings-integrations-view",
      },
    ],
  },
];

// Playground pages registry
// When adding a new page to /pages/[vendor]/playground/:
// 1. Add an entry here with id (filename with - replaced by _) and a label
const playgroundPages = [
  { id: "colors", label: "Colors", href: "/playground/colors" },
  {
    id: "entity_selector",
    label: "Entity Selector",
    href: "/playground/entity-selector",
  },
  {
    id: "sesamy_components",
    label: "Portal Components",
    href: "/playground/sesamy-components",
  },
  { id: "upload", label: "Upload", href: "/playground/upload" },
];

if (IS_DEVELOPMENT) {
  navItems.push({
    id: "playground",
    label: "Playground",
    icon: "smart",
    scopes: [],
    subItems: playgroundPages.map(({ id, label, href }) => ({
      id,
      label,
      href,
      scopes: [],
    })),
  });
}

export default navItems;
